import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import LanguageToggle from '@/components/LanguageToggle'
import { useAuth } from '@/contexts/AuthContext'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'
import { supabase, initialAuthRedirect } from '@/lib/supabase'
import { isInIframe } from '@/utils/iframeDetector'

type LinkStatus = 'checking' | 'valid' | 'invalid'

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth')
  const { updatePassword } = useAuth()
  const navigate = useLocalizedNavigate()

  const [linkStatus, setLinkStatus] = useState<LinkStatus>('checking')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // The recovery link signs the user in with a short-lived session. If that
  // session is missing, the link was invalid, already used, or expired.
  useEffect(() => {
    let mounted = true

    const checkRecoverySession = async () => {
      if (initialAuthRedirect.errorCode) {
        if (mounted) setLinkStatus('invalid')
        return
      }

      // getSession() waits for the client to finish parsing the URL hash
      const { data: { session } } = await supabase.auth.getSession()
      if (!mounted) return

      if (session) {
        setLinkStatus('valid')
        // Keep the access token out of the address bar / referrer
        window.history.replaceState({}, document.title, window.location.pathname)
      } else {
        setLinkStatus('invalid')
      }
    }

    checkRecoverySession()

    return () => {
      mounted = false
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (password !== confirmPassword) {
      setError(t('errors.passwordMismatch'))
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('errors.passwordTooShort'))
      setLoading(false)
      return
    }

    try {
      const { error } = await updatePassword(password)

      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
      }
    } catch (err) {
      console.error('Unexpected error updating password:', err)
      setError(t('errors.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  const wrap = (children: React.ReactNode) => (
    <div className="min-h-screen bg-white relative">
      {!isInIframe() && (
        <div className="absolute top-4 right-4">
          <LanguageToggle />
        </div>
      )}
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full">{children}</div>
      </div>
    </div>
  )

  if (linkStatus === 'checking') {
    return wrap(
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
        <p className="text-gray-600">{t('resetPassword.verifying')}</p>
      </div>
    )
  }

  if (linkStatus === 'invalid') {
    return wrap(
      <div className="text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {t('resetPassword.invalidTitle')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-6">
          {initialAuthRedirect.errorDescription || t('resetPassword.invalidMessage')}
        </p>

        <Button
          onClick={() => navigate('/forgot-password')}
          className="w-full bg-gray-800 hover:bg-gray-900"
        >
          {t('resetPassword.requestNewLink')}
        </Button>
      </div>
    )
  }

  if (success) {
    return wrap(
      <div className="text-center">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {t('resetPassword.successTitle')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-6">
          {t('resetPassword.successMessage')}
        </p>

        <Button onClick={() => navigate('/')} className="w-full bg-gray-800 hover:bg-gray-900">
          {t('resetPassword.continueButton')}
        </Button>
      </div>
    )
  }

  return wrap(
    <>
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          {t('resetPassword.title')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600">{t('resetPassword.subtitle')}</p>
      </div>

      {/* New Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            {t('resetPassword.passwordLabel')}
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('resetPassword.passwordPlaceholder')}
            required
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">{t('resetPassword.passwordHint')}</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
            {t('resetPassword.confirmPasswordLabel')}
          </label>
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            required
            className="w-full"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300"
        >
          {loading ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
        </Button>
      </form>
    </>
  )
}

export default ResetPasswordPage
